/**
 * MONSTER TOWN — THE VOICE CREW. `installVoice(ctx)`, and nothing else.
 *
 * Reads `VOICE_TRIGGERS` (data, in `./triggers.ts`), subscribes one listener per
 * distinct event, and emits `audio:voice` with a key. That is the whole crew.
 *
 * IT CANNOT AFFECT THE GAME. It only ever *listens*, and the one thing it emits
 * is `audio:voice`, which the audio crew answers by playing a recorded clip and
 * ducking the music bed. There is no path from this folder to a panel, a walk, a
 * placement, a coin or a save. LAW #4 is therefore structural rather than a
 * promise: delete `src/voice/**` and the game is byte-for-byte the same game,
 * without the voice. `voice-check` plays it muted to prove the other half — that
 * a kid who never hears a word can still do everything.
 *
 * IT IS NEVER A NAG (LAW #2). At most one line is in flight; a second line
 * arriving inside `SETTLE_MS` is dropped unless it outranks the one that just
 * went (a tutorial beat outranks a coin flourish). The player itself then adds
 * a floor between lines and a nine-second per-key cooldown, so hammering the
 * same prop gets the tap sound every time and the sentence once.
 */

import { bus, type EventName } from '../core/events'
import type { GameContext } from '../app/context'
import { VOICE_TRIGGERS, type AnyVoiceTrigger, type TriggerContext } from './triggers'

/** Two lines landing inside this window: the higher-ranked one wins. */
const SETTLE_MS = 900

interface Row {
  readonly t: AnyVoiceTrigger
  readonly priority: number
}

let lastSaidAt = 0
let lastPriority = 9

/** The little context the table is allowed to ask about. */
let openPanel: string | null = null
const npcNames = new Map<string, string>()

const ctx: TriggerContext = {
  get panel() {
    return openPanel
  },
  nameOf: (npcId) => npcNames.get(npcId) ?? null,
}

function say(key: string, priority: number): void {
  const now = performance.now()
  if (now - lastSaidAt < SETTLE_MS && priority >= lastPriority) return
  lastSaidAt = now
  lastPriority = priority
  bus.emit('audio:voice', { key })
}

export function installVoice(_ctx: GameContext): void {
  // The two bits of state the table reads. Both are pure observation.
  bus.on('ui:panel', (e) => {
    openPanel = e.panel
  })
  bus.on('npc:movedIn', (e) => {
    npcNames.set(e.npcId, e.name)
  })

  // One bus subscription per distinct event, however many rows use it.
  const byEvent = new Map<EventName, Row[]>()
  for (const t of VOICE_TRIGGERS) {
    const list = byEvent.get(t.on) ?? []
    list.push({ t, priority: t.priority ?? 2 })
    byEvent.set(t.on, list)
  }

  for (const [name, rows] of byEvent) {
    // The payload type is recovered per row by the `trigger()` helper that built
    // it; this one cast is the seam between a heterogeneous table and a typed bus.
    bus.on(name as 'app:booted', (payload) => {
      const p = payload as never
      for (const row of rows) {
        const t = row.t as VoiceTriggerLike
        if (t.when !== undefined && !t.when(p, ctx)) continue
        const key = typeof t.say === 'function' ? t.say(p, ctx) : t.say
        if (key !== null && key !== undefined && key.length > 0) say(key, row.priority)
      }
    })
  }

  window.__voiceWiring = {
    ready: true,
    triggers: () => VOICE_TRIGGERS.map((t) => ({ id: t.id, on: String(t.on) })),
    panel: () => openPanel,
    names: () => [...npcNames.values()],
  }
}

/** The shape `trigger()` erases. Local to the one cast above. */
interface VoiceTriggerLike {
  when?: (p: never, c: TriggerContext) => boolean
  say: string | ((p: never, c: TriggerContext) => string | null)
}

export interface VoiceWiringApi {
  readonly ready: boolean
  triggers(): { id: string; on: string }[]
  panel(): string | null
  names(): string[]
}

declare global {
  interface Window {
    __voiceWiring?: VoiceWiringApi
  }
}
